/**
 * KB.List.View.js
 *
 * Registered for the ext-kb-list page type. Handles routes:
 *   /resources                    — listing (all topics + grouped articles)
 *   /resources/topic/:topicSlug   — filtered by topic
 *   /resources/:slug              — individual article (fallback when no CMS page)
 *
 * Pulls the published static JSON at /site/kb-data.json.
 */
define('KB.List.View', [
    'kb_list_page.tpl',
    'kb_article_detail.tpl',
    'KB.Icons',
    'Backbone',
    'jQuery',
    'underscore',
    'Utils'
], function (
    listPageTemplate,
    articleDetailTemplate,
    KBIcons,
    Backbone,
    jQuery,
    _,
    Utils
) {
    'use strict';

    // Pad a 1-based index to a 2-digit zero-padded string ("01", "02", …)
    function pad2(n) {
        n = Number(n) || 0;
        return (n < 10 ? '0' : '') + n;
    }

    return Backbone.View.extend({
        template: listPageTemplate,
        title: 'Resources',
        page_header: 'Resources',

        events: {
            'input #kb-search': 'handleSearch',
            'click .kb-faq-question': 'toggleFaq',
            'click .kb-article-body a[href^="#"]': 'handleAnchorScroll'
        },

        initialize: function (options) {
            this.application = options.application;
            this.data = { topics: [], articles: [] };

            // Determine mode from the current URL fragment
            var fragment = Backbone.history.getFragment();
            var normalizedFragment = fragment.replace(/^academy\//, '');
            var topicMatch   = normalizedFragment.match(/^resources\/topic\/([A-Za-z0-9\-_.]+)/);
            var articleMatch = normalizedFragment.match(/^resources\/([A-Za-z0-9\-_.]+)/);

            if (topicMatch) {
                this.mode = 'topic';
                this.topicSlug = topicMatch[1];
            } else if (articleMatch && !(/^resources\/?(\?|$)/).test(normalizedFragment)) {
                this.mode = 'article';
                this.articleSlug = articleMatch[1];
            } else {
                this.mode = 'list';
            }
        },

        beforeShowContent: function () {
            var self = this;
            var promise = jQuery.Deferred();

            // Static JSON published by the KB Admin Suitelet (action=publishjson).
            // Same-origin with the SC site — no auth, no JSONP, no CORS.
            var serviceUrl = '/site/kb-data.json';

            jQuery.ajax({
                url: serviceUrl,
                dataType: 'json',
                cache: false
            }).done(function (data) {
                try {
                    data = data || {};
                    if (!data.topics) data.topics = [];
                    if (!data.articles) data.articles = [];
                    self.data = data;

                    if (self.mode === 'article') {
                        var match = _.find(data.articles, function (a) {
                            return a && a.slug === self.articleSlug;
                        });
                        if (match) {
                            var topic = _.find(data.topics, function (t) {
                                return t && String(t.id) === String(match.topicId);
                            });
                            if (topic) {
                                match.topicName = topic.name;
                                match.topicSlug = topic.slug;
                                match.topicIcon = topic.icon;
                            }
                            self.data.article = match;
                            self.template = articleDetailTemplate;
                            self.title = (match.title || 'Article') + ' | Resources';
                        } else {
                            // Unknown slug — fall back to the listing but say so,
                            // instead of silently rendering the landing page.
                            self.mode = 'list';
                            self.notFound = true;
                            self.requestedSlug = self.articleSlug;
                            self.title = 'Not Found | Resources';
                        }
                    } else if (self.mode === 'topic') {
                        var activeTopic = _.find(data.topics, function (t) {
                            return t && t.slug === self.topicSlug;
                        });
                        if (activeTopic) {
                            self.title = activeTopic.name + ' | Resources';
                        }
                    }
                } catch (e) {
                    if (window.console && console.error) {
                        console.error('KB.List.View beforeShowContent:', e);
                    }
                    self.data = { topics: [], articles: [] };
                }
                promise.resolve();
            }).fail(function () {
                promise.resolve();
            });

            return promise;
        },

        getTitle: function () { return this.title; },

        getBreadcrumb: function () {
            var crumbs = [
                { text: 'Home', href: '/' },
                { text: 'Resources', href: '/resources' }
            ];

            if (this.mode === 'article' && this.data.article) {
                var a = this.data.article;
                if (a.topicName && a.topicSlug) {
                    crumbs.push({ text: a.topicName, href: '/resources/topic/' + a.topicSlug });
                }
                crumbs.push({ text: a.title });
            } else if (this.mode === 'topic' && this.data.topics && this.data.topics.length) {
                var activeTopic = _.find(this.data.topics, function (t) {
                    return t.slug === this.topicSlug;
                }, this);
                if (activeTopic) crumbs.push({ text: activeTopic.name });
            }

            return crumbs;
        },

        getContext: function () {
            if (!this.data) this.data = { topics: [], articles: [] };

            // ── Article detail mode ─────────────────────────────────────
            if (this.mode === 'article' && this.data.article) {
                var article = this.data.article;
                // Ref number must match the listing badge: position within
                // the topic, counted separately per type (A.. / Q..), in the
                // same (sort-ordered) sequence the listing iterates.
                var siblings = (this.data.articles || []).filter(function (x) {
                    return String(x.topicId) === String(article.topicId) &&
                           x.typeId === article.typeId;
                });
                var refPos = 0;
                for (var s = 0; s < siblings.length; s++) {
                    if (String(siblings[s].id) === String(article.id)) { refPos = s + 1; break; }
                }
                return {
                    isArticle: true,
                    isList: false,
                    title: article.title,
                    content: article.content,
                    question: article.question,
                    topicName: article.topicName || '',
                    topicSlug: article.topicSlug || '',
                    topicIconSvg: KBIcons.get(article.topicIcon, 'kb-svg'),
                    isFaq: article.typeId === '1',
                    slug: article.slug,
                    articleRef: (article.typeId === '1' ? 'Q' : 'A') + pad2(refPos || 1),
                    arrowLeftSvg: KBIcons.get('arrowLeft', 'kb-svg'),
                    arrowRightSvg: KBIcons.get('arrowRight', 'kb-svg')
                };
            }

            // ── Listing / topic-filter mode ─────────────────────────────
            var topics   = this.data.topics   || [];
            var articles = this.data.articles || [];
            var isTopicFilter   = this.mode === 'topic';
            var activeTopicSlug = this.topicSlug || '';

            // Article counts per topic
            var countMap = {};
            articles.forEach(function (a) {
                countMap[a.topicId] = (countMap[a.topicId] || 0) + 1;
            });

            // Decorate topics with computed fields. Icon only renders when the
            // admin actually set one — no icon means no glyph, not the fallback.
            var topicsDecorated = topics.map(function (t) {
                var count = countMap[t.id] || 0;
                return _.extend({}, t, {
                    articleCount: count,
                    countLabel: count === 1 ? 'entry' : 'entries',
                    isActive: isTopicFilter && t.slug === activeTopicSlug,
                    iconSvg: t.icon ? KBIcons.get(t.icon, 'kb-svg') : ''
                });
            });

            // The topic grid only shows topics that have at least one entry.
            // (indexLabel is assigned after filtering so T01..Tnn stays gapless.)
            var topicsWithCounts = topicsDecorated
                .filter(function (t) { return t.articleCount > 0; })
                .map(function (t, idx) {
                    return _.extend(t, { indexLabel: 'T' + pad2(idx + 1) });
                });

            // Find the active topic (for the topic-filter hero) — searched in
            // the unfiltered list so an emptied topic page still gets its hero.
            var activeTopic = null;
            if (isTopicFilter) {
                activeTopic = _.find(topicsDecorated, function (t) {
                    return t.slug === activeTopicSlug;
                }) || null;
            }

            // Build groups (sorted topics with their decorated articles).
            // On the /resources index each group shows at most LIBRARY_CAP
            // entries; the rest are rendered hidden (so client-side search
            // still finds them) behind a "View all" link to the topic page.
            var LIBRARY_CAP = 4;
            var topicGroups = [];
            var groupIdx = 0;
            topics.forEach(function (topic, tIdx) {
                var topicArticles = articles.filter(function (a) {
                    return String(a.topicId) === String(topic.id);
                });
                if (topicArticles.length === 0) return;

                // Skip non-matching topics in filter mode
                if (isTopicFilter && topic.slug !== activeTopicSlug) return;

                groupIdx++;
                var articleIdx = 0;
                var faqIdx = 0;
                var capped = !isTopicFilter && topicArticles.length > LIBRARY_CAP;

                topicGroups.push({
                    topic: _.extend({}, topic, {
                        iconSvg: topic.icon ? KBIcons.get(topic.icon, 'kb-svg') : ''
                    }),
                    groupNum: pad2(groupIdx),
                    hasMore: capped,
                    moreCount: capped ? topicArticles.length - LIBRARY_CAP : 0,
                    articleTotal: topicArticles.length,
                    topicUrl: '/resources/topic/' + topic.slug,
                    articles: topicArticles.map(function (a, aIdx) {
                        var isFaq = a.typeId === '1';
                        var excerpt = '';
                        if (!isFaq && typeof a.content === 'string' && a.content) {
                            // Strip tags, then DECODE entities — the stored HTML's
                            // text nodes are entity-encoded (&amp;), and Handlebars
                            // escapes again on output, so without decoding the
                            // visitor sees a literal "&amp;". textarea.innerHTML
                            // decodes without ever executing markup.
                            var stripped = a.content.replace(/<[^>]+>/g, ' ');
                            var ta = document.createElement('textarea');
                            ta.innerHTML = stripped;
                            excerpt = ta.value.replace(/\s+/g, ' ').trim();
                            if (excerpt.length > 180) {
                                // cut at a word boundary, never mid-word
                                excerpt = excerpt.substring(0, 180).replace(/\s+\S*$/, '') + ' …';
                            }
                        }
                        var ref;
                        if (isFaq) {
                            faqIdx++;
                            ref = 'Q' + pad2(faqIdx);
                        } else {
                            articleIdx++;
                            ref = 'A' + pad2(articleIdx);
                        }
                        return {
                            id: a.id,
                            title: a.title,
                            slug: a.slug,
                            typeId: a.typeId,
                            isFaq: isFaq,
                            question: a.question,
                            content: a.content,
                            excerpt: excerpt,
                            ref: ref,
                            url: '/resources/' + a.slug,
                            overflow: capped && aIdx >= LIBRARY_CAP
                        };
                    })
                });
            });

            return {
                isArticle: false,
                isList: true,
                notFound: !!this.notFound,
                requestedSlug: this.requestedSlug || '',
                isTopicFilter: isTopicFilter,
                activeTopicSlug: activeTopicSlug,
                activeTopic: activeTopic,
                topics: topicsWithCounts,
                topicGroups: topicGroups,
                hasTopics: topicsWithCounts.length > 0,
                hasArticles: articles.length > 0,
                arrowRightSvg: KBIcons.get('arrowRight', 'kb-svg'),
                arrowLeftSvg: KBIcons.get('arrowLeft', 'kb-svg'),
                searchSvg: KBIcons.get('search', 'kb-svg'),
                plusSvg: KBIcons.get('plus', 'kb-svg')
            };
        },

        // ── Event handlers ──────────────────────────────────────────
        handleSearch: function (e) {
            var query = jQuery(e.target).val().toLowerCase().trim();
            if (!query) {
                jQuery('.kb-faq-item, .kb-article-row, .kb-topic-group, .kb-topic-card').show();
                // restore the per-topic cap and its "View all" links
                jQuery('.kb-row-overflow').hide();
                jQuery('.kb-group-more').show();
                return;
            }
            // While searching, the cap is suspended: capped (hidden) rows that
            // match are revealed, and the "View all" links get out of the way.
            jQuery('.kb-group-more').hide();
            jQuery('.kb-faq-item, .kb-article-row').each(function () {
                var text = jQuery(this).text().toLowerCase();
                jQuery(this).toggle(text.indexOf(query) !== -1);
            });
            jQuery('.kb-topic-group').each(function () {
                var visible = jQuery(this).find('.kb-faq-item:visible, .kb-article-row:visible').length;
                jQuery(this).toggle(visible > 0);
            });
        },

        toggleFaq: function (e) {
            jQuery(e.currentTarget).closest('.kb-faq-item').toggleClass('kb-faq-open');
        },

        /**
         * In-page anchor scroll for TOC links inside article bodies.
         *
         * Why: SCS's global router intercepts every internal <a> click and
         * tries to match its href to a Backbone route. `href="#kb-s3"` resolves
         * to `/kb-s3` under pushState, which 404s. We catch the click first
         * (delegated events fire during bubble before document-level handlers),
         * scroll the target into view, and replace the URL with replaceState
         * so reload / back-button preserves the position without rerouting.
         */
        handleAnchorScroll: function (e) {
            var a = e.currentTarget;
            var href = a && a.getAttribute('href');
            if (!href || href === '#' || href.length < 2) return;

            var targetId = href.substring(1);
            var target = document.getElementById(targetId);
            if (!target) return;

            e.preventDefault();
            e.stopPropagation();
            if (e.stopImmediatePropagation) e.stopImmediatePropagation();

            target.scrollIntoView({ behavior: 'smooth', block: 'start' });

            try {
                if (history && history.replaceState) {
                    history.replaceState(null, '', window.location.pathname + href);
                }
            } catch (err) { /* old browser — no-op */ }
        }
    });
});
